import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Paper,
  Stack,
  Alert,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { uploadNotebook, getCourses, getCourseRuns, getLectures } from '../services/api';
import { Course, CourseRun, Lecture } from '../types';

export const UploadPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState<number | ''>('');
  const [selectedCourseRunId, setSelectedCourseRunId] = useState<number | ''>('');
  const [selectedLectureId, setSelectedLectureId] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [courseRuns, setCourseRuns] = useState<CourseRun[]>([]);
  const [lectures, setLectures] = useState<Lecture[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Загрузка списка курсов при монтировании компонента
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const coursesData = await getCourses();
        setCourses(coursesData);
      } catch (err) {
        console.error('Ошибка при загрузке курсов:', err);
        setError('Не удалось загрузить список курсов');
      }
    };
    
    fetchCourses();
  }, []);
  
  // Загрузка потоков курса при выборе курса
  useEffect(() => {
    const fetchCourseRuns = async () => {
      if (selectedCourseId === '') return;
      
      try {
        const courseRunsData = await getCourseRuns(selectedCourseId as number);
        setCourseRuns(courseRunsData);
        setSelectedCourseRunId('');
        setSelectedLectureId('');
        setLectures([]);
      } catch (err) {
        console.error('Ошибка при загрузке потоков курса:', err);
        setError('Не удалось загрузить список потоков курса');
      }
    };
    
    fetchCourseRuns();
  }, [selectedCourseId]);
  
  // Загрузка лекций при выборе потока курса
  useEffect(() => {
    const fetchLectures = async () => {
      if (selectedCourseRunId === '') return;
      
      try {
        const lecturesData = await getLectures(selectedCourseRunId as number);
        setLectures(lecturesData);
        setSelectedLectureId('');
      } catch (err) {
        console.error('Ошибка при загрузке лекций:', err);
        setError('Не удалось загрузить список лекций');
      }
    };
    
    fetchLectures();
  }, [selectedCourseRunId]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    processFile(file);
  };

  const processFile = (file?: File) => {
    if (file) {
      if (!file.name.endsWith('.ipynb')) {
        setError('Пожалуйста, выберите файл с расширением .ipynb');
        return;
      }
      setSelectedFile(file);
      setTitle(file.name.replace('.ipynb', ''));
      setError(null);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      processFile(file);
      
      // Обновляем значение input file для согласованности UI
      if (fileInputRef.current) {
        // Создаем новый FileList (это не так просто, поэтому просто сбрасываем)
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !title.trim()) {
      setError('Пожалуйста, выберите файл и введите название');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await uploadNotebook(
        selectedFile, 
        title, 
        selectedLectureId !== '' ? selectedLectureId : undefined
      );
      navigate(`/notebooks/${response.id}`);
    } catch (err) {
      console.error('Ошибка при загрузке ноутбука:', err);
      setError('Произошла ошибка при загрузке ноутбука. Пожалуйста, попробуйте снова.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Paper sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Загрузка ноутбука
        </Typography>
        
        <Stack spacing={3} sx={{ mt: 4 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box 
            sx={{ 
              textAlign: 'center',
              border: isDragging ? '2px dashed #1976d2' : '2px dashed #e0e0e0',
              borderRadius: '8px',
              p: 4,
              backgroundColor: isDragging ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              accept=".ipynb"
              id="notebook-file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            
            <Typography variant="h6" gutterBottom color={isDragging ? 'primary' : 'textSecondary'}>
              {isDragging ? 'Отпустите файл здесь' : 'Перетащите файл ноутбука сюда'}
            </Typography>
            
            <Typography variant="body2" color="textSecondary" gutterBottom>
              или
            </Typography>
            
            <Button
              variant="outlined"
              component="span"
              disabled={loading}
              sx={{ mt: 1 }}
            >
              Выбрать ноутбук
            </Button>

            {selectedFile && (
              <Typography variant="body1" sx={{ mt: 2, fontWeight: 'bold', color: 'success.main' }}>
                Выбран файл: {selectedFile.name}
              </Typography>
            )}
          </Box>
          
          <TextField
            label="Название ноутбука"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            disabled={loading}
          />
          
          <FormControl fullWidth>
            <InputLabel id="course-select-label">Курс</InputLabel>
            <Select
              labelId="course-select-label"
              id="course-select"
              value={selectedCourseId}
              label="Курс"
              onChange={(e) => setSelectedCourseId(e.target.value as number)}
              disabled={loading || courses.length === 0}
            >
              {courses.map((course) => (
                <MenuItem key={course.id} value={course.id}>
                  {course.title}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Выберите курс</FormHelperText>
          </FormControl>
          
          <FormControl fullWidth disabled={selectedCourseId === '' || loading}>
            <InputLabel id="course-run-select-label">Поток курса</InputLabel>
            <Select
              labelId="course-run-select-label"
              id="course-run-select"
              value={selectedCourseRunId}
              label="Поток курса"
              onChange={(e) => setSelectedCourseRunId(e.target.value as number)}
              disabled={selectedCourseId === '' || loading || courseRuns.length === 0}
            >
              {courseRuns.map((courseRun) => (
                <MenuItem key={courseRun.id} value={courseRun.id}>
                  {courseRun.title}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Выберите поток курса</FormHelperText>
          </FormControl>
          
          <FormControl fullWidth disabled={selectedCourseRunId === '' || loading}>
            <InputLabel id="lecture-select-label">Лекция</InputLabel>
            <Select
              labelId="lecture-select-label"
              id="lecture-select"
              value={selectedLectureId}
              label="Лекция"
              onChange={(e) => setSelectedLectureId(e.target.value as number)}
              disabled={selectedCourseRunId === '' || loading || lectures.length === 0}
            >
              {lectures.map((lecture) => (
                <MenuItem key={lecture.id} value={lecture.id}>
                  {lecture.title}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Выберите лекцию</FormHelperText>
          </FormControl>
          
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || !title.trim() || loading}
            sx={{ mt: 2 }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Загрузить ноутбук'
            )}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}; 